import React, { useState, useRef, useEffect } from "react";
import "./Epitome.css";
import questionsData from "../data/Epitome_questions.json";
// import questionsData from "../data/sample_questions.json";
import config from "../config";

const QUESTIONS_PER_PAGE = 10;

const Epitome = () => {
  const [questions] = useState(questionsData.questions);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [userEmail, setUserEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showErrors, setShowErrors] = useState(false);
  const [unansweredWarning, setUnansweredWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousPage, setPreviousPage] = useState(1);
  const [consentGiven, setConsentGiven] = useState(null);

  const questionContainerRef = useRef(null);
  const descriptionRef = useRef(null);

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

  useEffect(() => {
    // No initial scroll
  }, []);

  useEffect(() => {
    if (
      currentPage > 1 &&
      currentPage !== previousPage &&
      descriptionRef.current
    ) {
      descriptionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setPreviousPage(currentPage);
  }, [currentPage, previousPage]);

  const handleInputChange = (
    questionNum,
    value,
    isCheckbox = false,
    questionType
  ) => {
    setErrors((prev) => ({ ...prev, [questionNum]: "" }));
    setUnansweredWarning(false);

    if (isCheckbox) {
      // Special logic for questions with "none" option that should exclude other options
      if (questionNum === 27 || questionNum === 37 || questionNum === 43 || questionNum === 68 || questionNum === 69) {
        const isNone = value === "none";
        
        setAnswers((prev) => {
          const currentAnswers = prev[questionNum] || [];
          const hasNone = currentAnswers.includes("none");
          
          if (isNone) {
            // If clicking "none", clear all other selections and only keep "none"
            return {
              ...prev,
              [questionNum]: hasNone ? [] : ["none"],
            };
          } else {
            // If clicking any other option
            let newAnswers;
            if (currentAnswers.includes(value)) {
              // Remove the clicked value
              newAnswers = currentAnswers.filter((item) => item !== value);
            } else {
              // Add the clicked value and remove "none" if present
              newAnswers = [...currentAnswers.filter((item) => item !== "none"), value];
            }
            return {
              ...prev,
              [questionNum]: newAnswers,
            };
          }
        });
      } else {
        // Normal checkbox behavior for other questions
        setAnswers((prev) => ({
          ...prev,
          [questionNum]: prev[questionNum]
            ? prev[questionNum].includes(value)
              ? prev[questionNum].filter((item) => item !== value)
              : [...prev[questionNum], value]
            : [value],
        }));
      }
    } else {
      setAnswers((prev) => ({
        ...prev,
        [questionNum]: questionType === "text_field" ? value : [value],
      }));
    }
  };

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
    // Clear email mismatch error when user types
    if (errors.emailMismatch) {
      setErrors((prev) => ({ ...prev, emailMismatch: "" }));
    }
  };

  const handleConfirmEmailChange = (e) => {
    setConfirmEmail(e.target.value);
    // Clear email mismatch error when user types
    if (errors.emailMismatch) {
      setErrors((prev) => ({ ...prev, emailMismatch: "" }));
    }
  };

  const validateCurrentPageAnswers = () => {
    const newErrors = {};
    let isValid = true;
    let firstUnansweredQuestion = null;

    const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
    const endIndex = startIndex + QUESTIONS_PER_PAGE;
    const pageQuestions = questions.slice(startIndex, endIndex);

    pageQuestions.forEach((question) => {
      if (
        !answers[question.question_num] ||
        (Array.isArray(answers[question.question_num]) &&
          answers[question.question_num].length === 0) ||
        (question.type === "text_field" &&
          (!answers[question.question_num] ||
            !answers[question.question_num].trim()))
      ) {
        newErrors[question.question_num] = "This question is required";
        isValid = false;
        if (!firstUnansweredQuestion) {
          firstUnansweredQuestion = question.question_num;
        }
      }
      
      // Special validation for question 19: must select exactly 2 answers
      if (question.question_num === 19 && answers[question.question_num]) {
        const selectedCount = Array.isArray(answers[question.question_num]) 
          ? answers[question.question_num].length 
          : 0;
        
        if (selectedCount !== 2) {
          newErrors[question.question_num] = "Please select exactly 2 options";
          isValid = false;
          if (!firstUnansweredQuestion) {
            firstUnansweredQuestion = question.question_num;
          }
        }
      }
    });

    setErrors(newErrors);
    setUnansweredWarning(!isValid);

    if (!isValid && firstUnansweredQuestion) {
      document.getElementById(`q${firstUnansweredQuestion}`).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return isValid;
  };

  const transformAnswersToBinary = () => {
    return questions.reduce((acc, question) => {
      const userAnswer = answers[question.question_num] || [];
      const binaryArray = question.answers.map((answer) =>
        Array.isArray(userAnswer)
          ? userAnswer.includes(answer)
            ? 1
            : 0
          : userAnswer[0] === answer
          ? 1
          : 0
      );
      acc[question.question_num] = binaryArray;
      return acc;
    }, {});
  };

  const handleNextPage = () => {
    if (currentPage === 1) {
      const newErrors = {};
      
      if (!userEmail) {
        newErrors.email = "Email is required";
      }
      
      if (!confirmEmail) {
        newErrors.confirmEmail = "Please confirm your email";
      }
      
      if (userEmail && confirmEmail && userEmail !== confirmEmail) {
        newErrors.emailMismatch = "Emails do not match";
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        document.getElementById("email").scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
    }

    if (!validateCurrentPageAnswers()) {
      setShowErrors(true);
      return;
    }

    setCurrentPage((prev) => prev + 1);
    setShowErrors(false);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => prev - 1);
    setShowErrors(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentPageAnswers()) {
      return;
    }

    // Check if consent is given
    if (consentGiven !== true) {
      setErrors({ consent: "You must consent to participate before submitting" });
      document.getElementById("consent-section")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setIsSubmitting(true);
    const transformedAnswers = transformAnswersToBinary();

    try {
      const response = await fetch(`${config.backendUrl}/epitome_assessment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_email: userEmail,
          answers: transformedAnswers,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        console.error("Submission failed:", await response.text());
        alert("Failed to submit questionnaire. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert(
        "Error submitting questionnaire. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    return (
      <React.Fragment key={question.question_num}>
        <div className="question-container" id={`q${question.question_num}`}>
          <p>
            {question.question_num}. {question.question}
          </p>
          <div className="options-container">
            {question.type === "single_choice" &&
              question.answers.map((answer, idx) => (
                <div
                  key={idx}
                  className={`option-box ${
                    answers[question.question_num]?.[0] === answer
                      ? "selected-option"
                      : ""
                  }`}
                  onClick={() =>
                    handleInputChange(
                      question.question_num,
                      answer,
                      false,
                      question.type
                    )
                  }
                >
                  {answer}
                </div>
              ))}

            {question.type === "multiple_choice" &&
              question.answers.map((answer, idx) => (
                <div
                  key={idx}
                  className={`option-box ${
                    answers[question.question_num]?.includes(answer)
                      ? "selected-option"
                      : ""
                  }`}
                  onClick={() =>
                    handleInputChange(
                      question.question_num,
                      answer,
                      true,
                      question.type
                    )
                  }
                >
                  {answer}
                </div>
              ))}

            {question.type === "text_field" && (
              <textarea
                id={`q${question.question_num}`}
                value={answers[question.question_num] || ""}
                onChange={(e) =>
                  handleInputChange(
                    question.question_num,
                    e.target.value,
                    false,
                    question.type
                  )
                }
                rows="4"
                placeholder="Enter your answer here..."
              />
            )}
          </div>
          {showErrors && errors[question.question_num] && (
            <div className="error-message">{errors[question.question_num]}</div>
          )}
        </div>
        {question.question_num === 32 && (
          <div className="info-message">
            Within the past 30 days (select only ONE answer except where indicated)
          </div>
        )}
        {question.question_num === 37 && (
          <div className="info-message">
            Within the past year (select only ONE answer except where indicated)
          </div>
        )}
        {question.question_num === 63 && (
          <div className="info-message">
            Secondary school years (13-19 years old)
          </div>
        )}
        {question.question_num === 74 && (
          <div className="info-message">
            Childhood (1-12 years old)
          </div>
        )}
        {question.question_num === 85 && (
          <div className="info-message">
            General
          </div>
        )}
      </React.Fragment>
    );
  };

  if (submitted) {
    return (
      <div className="epitome" style={{ minHeight: "auto" }}>
        <div className="epitome-content" style={{ padding: "1rem" }}>
          <div className="success-message">
            Thank you for your participation. We will contact you soon about your genetic test.
          </div>
        </div>
      </div>
    );
  }

  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const endIndex = startIndex + QUESTIONS_PER_PAGE;
  const pageQuestions = questions.slice(startIndex, endIndex);

  return (
    <div className="epitome" ref={questionContainerRef}>
      <div className="epitome-content">
        <form onSubmit={handleSubmit}>
          {currentPage === 1 && (
            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "2rem",
                borderRadius: "8px",
                marginBottom: "2rem",
                border: "1px solid #e9ecef",
                width: "200%",
                maxWidth: "200%",
                boxSizing: "border-box",
              }}
            >
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.7",
                  color: "#2c3e50",
                  margin: 0,
                }}
              >
                Welcome to the Epitome research project. The following questionnaire
                has 90 questions over 9 pages and can be completed on a desktop
                computer, cellphone, or tablet. It will take approximately 35
                minutes and needs to be completed in one sitting. Some questions
                refer back over 10 years, so please answer to the best of your
                recollection. All questions must be answered as the questionnaire
                won't advance to the next page unless all questions are answered on
                the current page. You can change answers by using the Previous Page
                button at the bottom of each page, making the desired change(s), and
                then moving back to where you left off using the Next Page button.
                Once you submit your answers, we will contact you to arrange your
                genetic test. Please get comfortable and begin when ready.
              </p>
            </div>
          )}

          {currentPage === 1 && (
            <>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={userEmail}
                  onChange={handleEmailChange}
                  required
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="confirmEmail">Confirm Email:</label>
                <input
                  type="email"
                  id="confirmEmail"
                  value={confirmEmail}
                  onChange={handleConfirmEmailChange}
                  required
                  placeholder="Re-enter your email address"
                />
                {errors.confirmEmail && (
                  <div className="error-message">{errors.confirmEmail}</div>
                )}
                {errors.emailMismatch && (
                  <div className="error-message">{errors.emailMismatch}</div>
                )}
              </div>
            </>
          )}

          <div className="description" ref={descriptionRef}>
            Please select the closest answer.
          </div>

          <div className="page-indicator">
            Page {currentPage} of {totalPages}
          </div>

          {pageQuestions.map(renderQuestion)}

          {currentPage === totalPages && (
            <div
              id="consent-section"
              style={{
                backgroundColor: "#fff9e6",
                padding: "2rem",
                borderRadius: "8px",
                marginTop: "2rem",
                marginBottom: "1rem",
                border: "2px solid #ffc107",
                width: "200%",
                maxWidth: "200%",
                boxSizing: "border-box",
              }}
            >
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.7",
                  color: "#2c3e50",
                  marginBottom: "1.5rem",
                  fontWeight: "500",
                }}
              >
                I understand that participation in the Epitome project is voluntary and that other than for
                use by my hockey team, I will remain anonymous once my data record, including my
                genetic data, is established. I understand that the system seeks to analyze why certain
                performance outcomes occur. I also understand that data is securely stored and used only
                for research and performance analysis purposes. I further understand that I will be paid
                200 Canadian Dollars as an honorarium for my participation. With these understandings I
                hereby consent to use of my data record.
              </p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                <div
                  className={`option-box ${consentGiven === true ? "selected-option" : ""}`}
                  onClick={() => {
                    setConsentGiven(true);
                    setErrors((prev) => ({ ...prev, consent: "" }));
                  }}
                  style={{ cursor: "pointer", minWidth: "120px", textAlign: "center" }}
                >
                  Yes, I consent
                </div>
                <div
                  className={`option-box ${consentGiven === false ? "selected-option" : ""}`}
                  onClick={() => {
                    setConsentGiven(false);
                    setErrors((prev) => ({ ...prev, consent: "" }));
                  }}
                  style={{ cursor: "pointer", minWidth: "120px", textAlign: "center" }}
                >
                  No, I do not consent
                </div>
              </div>
              {errors.consent && (
                <div className="error-message" style={{ marginTop: "1rem", textAlign: "center" }}>
                  {errors.consent}
                </div>
              )}
              {consentGiven === false && (
                <div 
                  className="warning-message" 
                  style={{ 
                    marginTop: "1rem", 
                    textAlign: "center",
                    backgroundColor: "#fff3cd",
                    color: "#856404",
                    border: "1px solid #ffeaa7"
                  }}
                >
                  You must consent to participate in order to submit the questionnaire.
                </div>
              )}
            </div>
          )}

          {unansweredWarning && (
            <div className="warning-message">Please answer all questions.</div>
          )}

          <div className="page-indicator">
            Page {currentPage} of {totalPages}
          </div>

          <div className="button-group">
            {currentPage > 1 && (
              <button
                type="button"
                onClick={handlePreviousPage}
                className="previous-page"
                disabled={isSubmitting}
              >
                Previous Page
              </button>
            )}
            <button
              type={currentPage === totalPages ? "submit" : "button"}
              onClick={currentPage < totalPages ? handleNextPage : handleSubmit}
              className={`next-page ${currentPage === 1 ? "centered" : ""}`}
              disabled={isSubmitting}
            >
              {currentPage < totalPages ? (
                "Next Page"
              ) : isSubmitting ? (
                <div className="button-content">
                  <div className="loading-spinner"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Epitome;
